import { type PermissionKey, permissionsForRealm } from "./permissions.ts";
import { PSI_PERMISSION_KEYS, type PsiPermissionKey } from "./psi-permissions.ts";

/**
 * The seeded roles. `07-entitlements-rbac.md` § 3: these are seeded, not
 * hard-coded — a store may edit them and add its own.
 */
export type SeededRole = {
  readonly code: string;
  readonly name: string;
  readonly permissions: readonly PermissionKey[];
  /**
   * Inventory rights, granted separately (`11-inventory.md` § 2). A role with
   * every POS permission still holds no inventory rights unless they are
   * listed here, which is what makes a stock controller possible.
   */
  readonly psiPermissions?: readonly PsiPermissionKey[];
};

const ALL_MERCHANT = permissionsForRealm("merchant").map((p) => p.key as PermissionKey);
const ALL_OPERATOR = permissionsForRealm("operator").map((p) => p.key as PermissionKey);
const ALL_PSI = [...PSI_PERMISSION_KEYS];

export const MERCHANT_ROLES: readonly SeededRole[] = [
  {
    code: "OWNER",
    name: "Owner",
    permissions: ALL_MERCHANT,
    psiPermissions: ALL_PSI,
  },
  {
    code: "MANAGER",
    name: "Manager",
    // Operations and reports. No billing, no role editing.
    permissions: ALL_MERCHANT.filter(
      (k) => !k.startsWith("billing:") && k !== "roles:write" && k !== "settings:currency",
    ),
    psiPermissions: ALL_PSI,
  },
  {
    code: "CASHIER",
    name: "Cashier",
    // POS only. Refund and void are granted separately, never by default.
    permissions: [
      "items:read",
      "categories:read",
      "modifiers:read",
      "tables:read",
      "orders:read",
      "orders:write",
      "settlement:pay",
      "settlement:drawer",
      "shifts:read",
      "shifts:write",
      // Everybody on a rota clocks themselves in. Reading a timesheet or
      // correcting one is a manager's job and is not granted here.
      "hr:clock",
      // Redeeming one is settlement; issuing one is not a cashier's call.
      "giftcards:read",
      "members:read",
      "printers:reprint",
    ],
  },
  {
    code: "WAITER",
    name: "Waiter",
    // Order taking and tables. No settlement, no reports.
    permissions: [
      "items:read",
      "categories:read",
      "modifiers:read",
      "tables:read",
      "orders:read",
      "orders:write",
      // Everybody on a rota clocks themselves in, and a waiter is on the rota.
      "hr:clock",
    ],
  },
  {
    code: "ACCOUNTANT",
    name: "Accountant",
    // Reports, exports and credit. Read-only elsewhere.
    permissions: [
      "reports:read",
      "reports:export",
      "reports:cost",
      "orders:read",
      "orders:export",
      "audit:read",
      "items:read",
      "categories:read",
      "members:read",
      "settings:read",
      "shifts:read",
      "billing:read",
    ],
    // Costs, not stock movements: an accountant reads what was bought and what
    // it was worth, and raises nothing.
    psiPermissions: ["psi:stock:read", "psi:purchase:read", "psi:supplier:read"],
  },
  {
    code: "STOCK_CONTROLLER",
    name: "Stock controller",
    /**
     * The role `11-inventory.md` § 2 exists to make possible: no till access at
     * all, and every inventory right except the two that sign work off —
     * approving a document and posting an adjustment stay with a manager.
     */
    permissions: ["items:read", "categories:read"],
    psiPermissions: ALL_PSI.filter(
      (k) => !k.endsWith(":approve") && k !== "psi:count:adjust" && k !== "psi:recipe:write",
    ),
  },
];

export const OPERATOR_ROLES: readonly SeededRole[] = [
  { code: "SUPERADMIN", name: "Superadmin", permissions: ALL_OPERATOR },
  {
    code: "BD",
    name: "Business Development",
    permissions: [
      "admin:merchants:read",
      "admin:merchants:write",
      "admin:plans:read",
      "admin:contracts:read",
      "admin:contracts:sign",
      "admin:subscriptions:read",
      "admin:referrals:read",
      "admin:referrals:write",
      "admin:dashboard:read",
    ],
  },
  {
    code: "AFTER_SALES",
    name: "After-sales",
    // Merchant support. No pricing, no settlement.
    permissions: [
      "admin:merchants:read",
      "admin:merchants:write",
      "admin:subscriptions:read",
      "admin:hardware:read",
      "admin:hardware:write",
      "admin:contracts:read",
      "admin:dashboard:read",
    ],
  },
  {
    code: "FINANCE",
    name: "Finance",
    // Reconciliation and settlement. Holds the OTP.
    permissions: [
      "admin:merchants:read",
      "admin:subscriptions:read",
      "admin:subscriptions:invalidate",
      "admin:contracts:read",
      "admin:partners:read",
      "admin:settlements:read",
      "admin:settlements:complete",
      "admin:reconciliation:read",
      "admin:dashboard:read",
    ],
  },
  {
    code: "CHANNEL_PARTNER",
    name: "Channel partner",
    // Own merchants and own commission statements only. The row scope is
    // enforced by the partner id on the session, not by this list.
    permissions: ["admin:merchants:read", "admin:settlements:read", "admin:referrals:read"],
  },
];
